import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "font-semibold inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 tracking-wide cursor-pointer border-none",
  {
    variants: {
      variant: {
        default: "",
        primary:
          "bg-black! text-white hover:bg-[rgb(52,53,54)]! active:bg-black/90!",
        secondary:
          "bg-[rgba(1,3,4,0.08)]! hover:bg-[rgba(1,3,4,0.12)]! text-[rgb(1,3,4)] font-semibold",
        tertiary: "hover:bg-[rgb(230, 230, 230)]!",
        theme: "bg-[#26ddf9]! hover:bg-[rgb(124,234,251)]! text-black"
      },
      size: {
        md: "h-10 px-4 text-sm!",
        sm: "h-8 px-4 text-xs!",
        lg: "h-14 px-8 text-base!",
        icon: "h-8 w-8"
      }
    },
    defaultVariants: {
      variant: "default", // primary should follow hubble primary, don't default to primary
      size: "md"
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
